import { createSlice, isRejectedWithValue } from "@reduxjs/toolkit";

const loadAuthState = () => {
  return {
    user: null,
    token: null,
    refreshToken: null,
    tokenExpiresAt: null,
    isAuthenticated: false,
    /** Saved Admin session while impersonating another user. Null when not impersonating. */
    impersonation: null,
  };
};

// Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState: loadAuthState(),
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiresAt = null;
      state.isAuthenticated = false;
      state.impersonation = null;
    },
    setToken: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      if (action.payload.expiresAt) {
        state.tokenExpiresAt = action.payload.expiresAt;
      }
      state.isAuthenticated = !!action.payload.token;
    },
    updateCurrentUser: (state, action) => {
      const firstName = action.payload.given_name;
      const lastName = action.payload.family_name;
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      state.user = {
        id: action.payload.sub,
        firstName,
        lastName,
        name: fullName,
        email: action.payload.email,
        role: action.payload.role,
        brokerCompanyStatus: action.payload.broker_company_status || null,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
        impersonatorSub: action.payload.impersonator_sub || null,
        impersonatorName: action.payload.impersonator_name || null,
      };
    },
    /**
     * Saves the current Admin session before swapping to an impersonation token.
     * Payload is unused — state is read from the current slice values.
     */
    startImpersonation: (state) => {
      if (state.impersonation) {
        // Already impersonating — keep the original Admin session.
        return;
      }
      state.impersonation = {
        originalToken: state.token,
        originalRefreshToken: state.refreshToken,
        originalTokenExpiresAt: state.tokenExpiresAt,
        originalUser: state.user,
      };
    },
    /**
     * Clears the saved Admin session snapshot after restoring it (or discarding it).
     */
    stopImpersonation: (state) => {
      state.impersonation = null;
    },
  },
});

export const selectIsTokenExpired = (state) => {
  return state.auth.tokenExpiresAt && Date.now() > state.auth.tokenExpiresAt;
};

export const selectIsImpersonating = (state) => !!state.auth.impersonation;

export const authMiddleware = (api) => (next) => (action) => {
  if (isRejectedWithValue(action) && action.payload?.isAuthError) {
    api.dispatch(logout());
    window.location.href = "/";
  }
  return next(action);
};

export const {
  logout,
  setToken,
  updateCurrentUser,
  startImpersonation,
  stopImpersonation,
} = authSlice.actions;
export default authSlice.reducer;
